import type { Claim, Payor } from 'interfaces';
import { validatePhoneNumber } from './validate';

export interface ParsedS3Attachment {
  bucket: string;
  key: string;
}

const ACCOUNT_ID = import.meta.env.VITE_ACCOUNT_ID;

export const INVOICE_BUCKET = `invoice-files-${ACCOUNT_ID}`;
export const WCB_BUCKET = `wcb-forms-${ACCOUNT_ID}`;
export const BLUE_CROSS_BUCKET = `bluecross-forms-${ACCOUNT_ID}`;
export const CUSTOM_FAX_RECIPIENT = 'custom:';

/**
 * Parse an S3 URI in the form s3://bucket/path/to/file and return bucket + key.
 */
export const parseFaxAttachmentS3Uri = (
  attachmentKey: string
): ParsedS3Attachment => {
  if (!attachmentKey || typeof attachmentKey !== 'string') {
    throw new Error('Fax attachment key must be a non-empty string');
  }

  const value = attachmentKey.trim();
  const s3Prefix = 's3://';

  if (!value.startsWith(s3Prefix)) {
    throw new Error('Fax attachment key must start with s3://');
  }

  const bucketAndKey = value.slice(s3Prefix.length);
  const firstSlash = bucketAndKey.indexOf('/');

  if (firstSlash <= 0) {
    throw new Error(
      'Fax attachment S3 URI must include both bucket and object key'
    );
  }

  const bucket = bucketAndKey.slice(0, firstSlash);
  const key = bucketAndKey.slice(firstSlash + 1);

  if (!key) {
    throw new Error('Fax attachment S3 URI object key cannot be empty');
  }

  return { bucket, key };
};

export const constructFaxAttachmentS3Uri = (
  bucket: string,
  key: string
): string => {
  if (!bucket || !key) {
    throw new Error('Both bucket and key are required to construct S3 URI');
  }
  return `s3://${bucket}/${key}`;
};

export const normalizeFaxNumber = (value?: string | null): string =>
  value?.trim().replace(/[^\d+]/g, '') || '';

export const buildFaxRecipientMap = (
  payors: Pick<Payor, 'id' | 'fax'>[]
): Map<string, string> => {
  const recipients = new Map<string, string>();
  payors.forEach((payor) => {
    recipients.set(payor.id, normalizeFaxNumber(payor.fax));
  });
  return recipients;
};

export const getClaimPayorFaxRecipient = (
  payors: Pick<Payor, 'id' | 'fax'>[],
  claimPayorID?: string | null
): { id: string; fax: string } | null => {
  const match = payors.find((payor) => payor.id === claimPayorID);
  const normalizedFax = normalizeFaxNumber(match?.fax);
  if (!match || !validatePhoneNumber(normalizedFax)) {
    return null;
  }
  return { id: match.id, fax: normalizedFax };
};

export const getInvoiceAttachmentBucketByRecipient = (): string => {
  return INVOICE_BUCKET;
};

export const getInvoiceAttachmentBucketByFileKey = (
  fileKey?: string | null
): string => {
  if (!fileKey) return INVOICE_BUCKET;
  if (fileKey.startsWith('wcb/')) return WCB_BUCKET;
  if (fileKey.startsWith('bluecross/')) return BLUE_CROSS_BUCKET;
  return INVOICE_BUCKET;
};

export const getLatestClaimInvoiceAttachmentKey = (
  claim: Pick<Claim, 'invoices'> | null
): string | null => {
  return claim?.invoices?.[0]?.file || null;
};
